// Kulturon theme – entry point. Bundled by esbuild (tools/build-js.mjs) to assets/dist/app.js as an ES module with code
// splitting: the static imports below are the PUBLIC CORE every visitor downloads; everything else is a lazy chunk
// (assets/dist/chunks/) loaded with import() only when its markup is on the page. Rules: CONVENTIONS.md → Performance.
// Modules are plain ES modules; each exports an init*() that is safe to call on every page.
import { ready, qs, on } from './modules/dom.js';
import { load, when } from './modules/lazy.js';
import { initOverlays } from './modules/overlays.js';
import { initCartDrawer } from './modules/cart-drawer.js';
import { initCartPage } from './modules/cart-page.js';
import { initCheckout } from './modules/checkout.js';
import { initHeader, initSearchOverlay, initNewsletter, initVideos, initScrollers, initRevealOnScroll, initPasswordToggle, initServerToasts, initArtistFilter } from './modules/ui.js';
import { initNavMenu } from './modules/nav-menu.js'; // 1.9.10 mega menu + mobile accordion (header on every page → core)
import { initHero } from './modules/hero.js';
import { initEventFilter } from './modules/event-filter.js';
import { initShop } from './modules/shop.js';
import { initHolders } from './modules/holders.js';
import { initGenreRows } from './modules/genre-rows.js';
import { initShare } from './modules/share.js';
import { initMediathek } from './modules/mediathek.js';
import { initArtFilter } from './modules/art-filter.js';
import { initTracking } from './modules/track.js'; // 1.9.8: GA4/Ads/Meta event queue + consent (third-party code = lazy chunks after consent)
import './modules/toast.js'; // window.ktToast
import './modules/modal.js'; // window.ktConfirm / ktAlert / ktPrompt (also for inline scripts)

// Account / back-office chunk (modules/account.js): the account shell or any of its widgets outside the shell.
const ACCOUNT = [
  '[data-account-content]', 'form[data-api]', 'button[data-api]', '[data-confirm]', '[data-repeater]', '[data-media-upload]',
  '[data-status-toggle]', '[data-kt-table]', '[data-boxoffice]', '[data-promo-book]', '[data-terminal-admin]', '[data-seatmap-editor]',
  '[data-seat-blocker]', '[data-media-check]', '[data-i18n-card]', '[data-rows-editor]', '[data-api-keys]', '[data-media-list]',
  '[data-oa-refunds]', '[data-series-add]', '[data-print-sort]', '[data-brevo-test]', '[data-copy-link]',
].join(',');

const picker = () => load('picker', () => import('./modules/ticket-picker.js'));

/** Inline ticket selector + seat map viewer (lazy chunk), loaded right away when the page has one (event page, branded event,
 *  special/custom pages). 1.9.11 (KULTUR-194): no drawer any more – "Ticket kaufen" buttons are links to the event page;
 *  premium tier links [data-ticket-preset] jump to #ticket-box (native anchor) and preselect their category. */
function initPickerLoader() {
  if (!qs('[data-ticket-selector][data-event-id]')) return;
  const booted = picker().then((m) => { m.initTicketPicker(); return m; });
  on(document, 'click', '[data-ticket-preset]', (e, el) => { booted.then((m) => m.presetSelector(el.dataset.ticketPreset, el.dataset.eventRef || '')); });
}

ready(() => {
  initOverlays();
  initHeader();
  initNavMenu();
  initSearchOverlay();
  initCartDrawer();
  initCartPage();
  initCheckout();
  initNewsletter();
  initVideos();
  initScrollers();
  initRevealOnScroll();
  initHero();
  initEventFilter();
  initPickerLoader();
  initShop();
  initHolders();
  initPasswordToggle();
  initServerToasts();
  initGenreRows();   // concept M
  initShare();       // concept P
  initMediathek();   // Mediathek: load more on /mediathek/
  initArtFilter();   // Standbein 2: /kunst/ filters
  initArtistFilter(); // KULTUR-202: /kuenstler/ category filter pills
  initTracking();    // 1.9.8: no-op without the #kt-trk config (no tool active / excluded area / staff)

  // Lazy public features (own chunks, only where their markup exists)
  when('[data-lightbox-open],[data-lightbox-gallery]', 'lightbox', () => import('./modules/lightbox.js'), (m) => m.initLightbox());
  when('form[data-organizer-apply]', 'organizer-apply', () => import('./modules/organizer-apply.js'), (m) => m.initOrganizerApply());
  when('form[data-artist-apply]', 'artist-apply', () => import('./modules/artist-apply.js'), (m) => m.initArtistApply()); // Standbein 2: /kuenstler-werden/
  when('form[data-location-suggest]', 'location-suggest', () => import('./modules/location-suggest.js'), (m) => m.initLocationSuggest()); // 1.9.11 KULTUR-187: /location-eintragen/
  when('[data-special-page]', 'special-page', () => import('./modules/special-page.js'), (m) => m.initSpecialPage()); // Premium landing (countdown, hero video, scrollspy, tabs)
  when('#kt-premium-request-tpl', 'premium-request', () => import('./modules/premium-request.js'), (m) => m.initPremiumRequest()); // "Jetzt anfragen"
  when('[data-org-directory]', 'organizer-directory', () => import('./modules/organizer-directory.js'), (m) => m.initOrganizerDirectory()); // 1.9.10 /veranstalter/ search, place filter, load more
  when('[data-an-optout]', 'analytics-optout', () => import('./modules/analytics-optout.js'), (m) => m.initAnalyticsOptout()); // /statistik-opt-out/
  // Account / dashboards / editors (one chunk)
  when(ACCOUNT, 'account', () => import('./modules/account.js'), (m) => m.initAccount());
});
